import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Card, DialogContent, IconButton, Typography} from "@mui/material";
import Api from "../../../../core/services/api/api";
import DateUtils from "../../../../core/services/date-utils";
import ToastService from "../../../../core/services/toast";
import BizPrintService from "../../../../core/services/biz-print/biz-print-service";
import useIsMounted from "../../../hooks/use-is-mounted";
import LoadingLottieAnimation from "../../app-specific/lottie-animations/loading";
import Grid2 from "@mui/material/Unstable_Grid2";
import {ReactComponent as SuccessIcon} from "../../../../assets/images/charge-credit-card/charge-success.svg";
import {ReactComponent as DeclinedIcon} from "../../../../assets/images/charge-credit-card/charge-declined.svg";
import Utils, {formatMoney, formatPhoneNumber} from "../../../../core/services/utils/utils";
import BizPrintTemplatesDialog from "../../dialogs/biz-print-templates";
import {PrintRounded} from "@mui/icons-material";
import useAccessLevels from "../../../hooks/use-access-levels";

const initialBizPrintDialogState = {
    open: false,
    pageName: null,
    pageData: null,
}

const TransactionReceiptInformation = ({receiptId, customerId, done}) => {
    const [receiptInfo, setReceiptInfo] = useState({client: null, merchant: null})
    const [customerInfo, setCustomerInfo] = useState(null)
    const [loadingCustomer, setLoadingCustomer] = useState(true);
    const [loadingReceipts, setLoadingReceipts] = useState(true);
    const [bizPrintDialogState, setBizPrintDialogState] = useState(initialBizPrintDialogState);
    const isMounted = useIsMounted();
    const {packages} = useAccessLevels();
    const canViewReceipts = packages.sales.paymentReceipts;

    const loading = useMemo(() => loadingCustomer || loadingReceipts, [loadingCustomer, loadingReceipts]);

    /**
     * Fetches the customer information for the client and merchant:
     * @type {(function(): Promise<void>)}
     */
    const getCustomerInfo = useCallback(async () => {
        if (!customerId) {
            setCustomerInfo(null);
            setLoadingCustomer(false);
            return;
        }
        setLoadingCustomer(true);
        const response = await Api.getCustomerFullInformation(customerId);
        if (!isMounted())
            return;
        setLoadingCustomer(false);
        if (!response?.resultFlag) {
            ToastService.error({
                title: 'Error',
                message: 'Failed to get customer information for the selected transaction.',
            });
            done();
            return;
        }
        setCustomerInfo(response?.data);
    }, [customerId, done, isMounted])

    /**
     * Fetches the customer information for the client and merchant.
     */
    useEffect(() => {
        void getCustomerInfo();
    }, [getCustomerInfo]);

    /**
     * Fetches the receipt information for the client and merchant:
     * @type {(function(): Promise<void>)}
     */
    const getReceiptInfo = useCallback(async () => {
        if (!receiptId || !customerId) {
            setReceiptInfo({client: null, merchant: null});
            setLoadingReceipts(false);
            return;
        }
        if (!canViewReceipts) {
            setReceiptInfo({client: null, merchant: null});
            setLoadingReceipts(false);
            return;
        }
        setLoadingReceipts(true);
        const [client, merchant] = await Promise.all([
            Api.getPOSClientReceipt(receiptId, customerId),
            Api.getPOSMerchantReceipt(receiptId, customerId),
        ]);
        if (!isMounted())
            return;
        setLoadingReceipts(false);
        if (!client?.resultFlag && !merchant?.resultFlag) {
            ToastService.error({
                title: 'Error',
                message: 'Failed to get receipt information for the selected transaction.',
            });
            done();
            return;
        }
        setReceiptInfo({client: client?.data, merchant: merchant?.data});
    }, [canViewReceipts, customerId, done, isMounted, receiptId])

    /**
     * Fetches the receipt information for the client and merchant.
     */
    useEffect(() => {
        void getReceiptInfo();
    }, [getReceiptInfo]);

    /**
     * navigates the user to the section where the user cna print the receipt.
     * @type {function(): void}
     */
    const printReceipt = useCallback(async (receipt) => {
        setBizPrintDialogState({
            open: true,
            pageName: BizPrintService.pages.transactionReceipt.name,
            pageData: {
                customerName: customerInfo?.name,
                customerAddress: `${customerInfo?.address}, ${customerInfo?.city}, ${customerInfo?.province}, ${customerInfo?.countryTitle}, ${customerInfo?.postalCode}`,
                businessName: receipt?.businessInformation?.name,
                businessAddress: `${receipt?.businessInformation?.address}, ${receipt?.businessInformation?.city}, ${receipt?.businessInformation?.province}, ${receipt?.businessInformation?.country}, ${receipt?.businessInformation?.postalCode}`,
                businessWebsite: Utils.formatLinkUrlDisplay(receipt?.businessInformation?.website),
                businessPhone: receipt?.businessInformation?.phone,
                businessEmail: receipt?.businessInformation?.email,
                transactionTitle: receipt?.isRefund ? `Refund Receipt From ${receipt?.businessInformation?.name}` : `Payment Receipt From ${receipt?.businessInformation?.name}`,
                transactionAuthCode: receipt?.authCode ? `Auth Code: ${receipt?.authCode}` : null,
                transactionDate: receipt?.rawSubmittedDateTime ? `${DateUtils.instance(receipt?.rawSubmittedDateTime).format(DateUtils.dateTimeFormat)} (${DateUtils.instance(receipt?.rawSubmittedDateTime).offsetName()})` : null,
                transactionTotalAmount: receipt?.amount,
                transactionType: receipt?.type,
                transactionReferenceNumber: receipt?.referenceNumber,
                transactionItemDescription: receipt?.isRefund ? `Refund from ${receipt?.businessInformation?.name}` : `Payment to ${receipt?.businessInformation?.name}`,
                transactionItemAmount: receipt?.amount,
                cardLast4Digits: receipt?.lastFourDigits,
                cardExpiry: receipt?.cardExpiry,
                cardType: receipt?.cardType,
                status: receipt?.terminalMessage,
            },
        })
    }, [customerInfo?.address, customerInfo?.city, customerInfo?.countryTitle, customerInfo?.name, customerInfo?.postalCode, customerInfo?.province])

    const succeeded = useMemo(() => receiptInfo?.merchant?.succeeded || receiptInfo?.client?.succeeded, [receiptInfo?.client?.succeeded, receiptInfo?.merchant?.succeeded]);

    return (
        <>
            <DialogContent className={'receipt-section'}>
                <div className={'content'}>
                    {
                        loading &&
                        <LoadingLottieAnimation width={'80%'}/>
                    }
                    {
                        !loading &&
                        <>
                            <Grid2 container spacing={4}>
                                <Grid2 xs={12}>
                                    <div className={'mt-3 d-flex flex-column align-items-center'}>
                                        {
                                            <p className={'transaction-status-title'}>
                                                {succeeded ? 'Transaction Successful' : 'Transaction Unsuccessful'}
                                            </p>
                                        }
                                        {succeeded ? <SuccessIcon/> : <DeclinedIcon/>}
                                    </div>
                                </Grid2>
                                {
                                    receiptInfo?.merchant &&
                                    <Grid2 xs={12} md={receiptInfo?.client ? 6 : 12}>
                                        <Card variant={'outlined'} sx={{padding: 2, backgroundColor: 'background.default', display: 'flex', flexDirection: 'column', gap: 2}}>
                                            <div className={'d-flex justify-content-between align-items-center'}>
                                                <Typography variant={'body1'} sx={{textAlign: 'start', width: '100%', fontWeight: 600, color: 'tertiary.main'}}>
                                                    For You (Merchant)
                                                </Typography>
                                                <IconButton
                                                    onClick={() => printReceipt(receiptInfo?.merchant)}
                                                    color={'paper.primary'}
                                                >
                                                    <PrintRounded/>
                                                </IconButton>
                                            </div>
                                            <div className={'receipt-summary'}>
                                                {
                                                    receiptInfo?.merchant?.businessInformation?.name &&
                                                    <p>
                                                        Business Name: <span>{receiptInfo?.merchant?.businessInformation?.name}</span>
                                                    </p>
                                                }
                                                {
                                                    receiptInfo?.merchant?.businessInformation &&
                                                    <p>
                                                        Business Address: <span>
                                                        {receiptInfo?.merchant?.businessInformation?.address ?? ""}, {receiptInfo?.merchant?.businessInformation?.city ?? ""}, {receiptInfo?.merchant?.businessInformation?.province ?? ""}, {receiptInfo?.merchant?.businessInformation?.country ?? ""}, {receiptInfo?.merchant?.businessInformation?.postalCode ?? ""}
                                                    </span>
                                                    </p>
                                                }
                                                {
                                                    receiptInfo?.merchant?.businessInformation?.email &&
                                                    <p>
                                                        Business Email: <span>{receiptInfo?.merchant?.businessInformation?.email}</span>
                                                    </p>
                                                }
                                                {
                                                    receiptInfo?.merchant?.businessInformation?.phone &&
                                                    <p>
                                                        Business Phone: <span>{formatPhoneNumber(receiptInfo?.merchant?.businessInformation?.phone)}</span>
                                                    </p>
                                                }
                                                {
                                                    receiptInfo?.merchant?.businessInformation?.website &&
                                                    <p>
                                                        Business Website: <span>{Utils.formatLinkUrlDisplay(receiptInfo?.merchant?.businessInformation?.website)}</span>
                                                    </p>
                                                }
                                                <br/>
                                                {
                                                    receiptInfo?.merchant?.invoiceReference &&
                                                    <p>
                                                        invoiceReference: <span>{receiptInfo?.merchant?.invoiceReference}</span>
                                                    </p>
                                                }
                                                {
                                                    receiptInfo?.merchant?.amount &&
                                                    <p>
                                                        amount: <span>{formatMoney(receiptInfo?.merchant?.amount)}</span>
                                                    </p>
                                                }
                                                {
                                                    receiptInfo?.merchant?.type &&
                                                    <p>
                                                        type: <span>{receiptInfo?.merchant?.type}</span>
                                                    </p>
                                                }
                                                {
                                                    receiptInfo?.merchant?.rawSubmittedDateTime &&
                                                    <p>
                                                        submit Date: <span>
                                                        {
                                                            `${DateUtils.instance(receiptInfo?.merchant?.rawSubmittedDateTime).format(DateUtils.dateTimeFormat)} (${DateUtils.instance(receiptInfo?.merchant?.rawSubmittedDateTime).offsetName()})`
                                                        }
                                                    </span>
                                                    </p>
                                                }
                                                {
                                                    receiptInfo?.merchant?.referenceNumber &&
                                                    <p>
                                                        reference Number: <span>{receiptInfo?.merchant?.referenceNumber}</span>
                                                    </p>
                                                }
                                                {
                                                    receiptInfo?.merchant?.authCode &&
                                                    <p>
                                                        auth Code: <span>{receiptInfo?.merchant?.authCode}</span>
                                                    </p>
                                                }
                                                {
                                                    receiptInfo?.merchant?.lastFourDigits &&
                                                    <p>
                                                        last Four Digits: <span>{receiptInfo?.merchant?.lastFourDigits}</span>
                                                    </p>
                                                }
                                                <br/>
                                                {
                                                    receiptInfo?.merchant?.iso &&
                                                    <p>
                                                        iso: <span>{receiptInfo?.merchant?.iso}</span>
                                                    </p>
                                                }
                                                {
                                                    receiptInfo?.merchant?.responseCode &&
                                                    <p>
                                                        response Code: <span>{receiptInfo?.merchant?.responseCode}</span>
                                                    </p>
                                                }
                                                {
                                                    receiptInfo?.merchant?.terminalMessage &&
                                                    <p>
                                                        Message: <span>{receiptInfo?.merchant?.terminalMessage}</span>
                                                    </p>
                                                }
                                                {
                                                    receiptInfo?.merchant?.receiptType &&
                                                    <p>
                                                        receipt Type: <span>{receiptInfo?.merchant?.receiptType}</span>
                                                    </p>
                                                }
                                                <br/>
                                                {
                                                    receiptInfo?.merchant?.txnNumber &&
                                                    <p>
                                                        txn Number: <span>{receiptInfo?.merchant?.txnNumber}</span>
                                                    </p>
                                                }
                                                {
                                                    receiptInfo?.merchant?.originalMessage &&
                                                    <p>
                                                        original Message: <span>{receiptInfo?.merchant?.originalMessage}</span>
                                                    </p>
                                                }
                                                {
                                                    receiptInfo?.merchant?.isVisaDebit &&
                                                    <p>
                                                        is Visa Debit: <span>{receiptInfo?.merchant?.isVisaDebit}</span>
                                                    </p>
                                                }
                                                {
                                                    receiptInfo?.merchant?.description &&
                                                    <p>
                                                        description: <span>{receiptInfo?.merchant?.description}</span>
                                                    </p>
                                                }
                                                {
                                                    receiptInfo?.merchant?.issuerId &&
                                                    <p>
                                                        issuer Id: <span>{receiptInfo?.merchant?.issuerId}</span>
                                                    </p>
                                                }
                                            </div>
                                        </Card>
                                    </Grid2>
                                }
                                {
                                    !!receiptInfo?.client &&
                                    <Grid2 xs={12} md={receiptInfo?.client ? 6 : 12}>
                                        <Card variant={'outlined'} sx={{padding: 2, backgroundColor: 'background.default', display: 'flex', flexDirection: 'column', gap: 2}}>
                                            <div className={'d-flex justify-content-between align-items-center'}>
                                                <Typography variant={'body1'} sx={{textAlign: 'start', width: '100%', fontWeight: 600, color: 'tertiary.main'}}>
                                                    For {customerInfo?.name} (Client)
                                                </Typography>
                                                <IconButton
                                                    onClick={() => printReceipt(receiptInfo?.client)}
                                                    color={'paper.primary'}
                                                >
                                                    <PrintRounded/>
                                                </IconButton>
                                            </div>
                                            <div className={'receipt-summary'}>
                                                {
                                                    receiptInfo?.client?.businessInformation?.name &&
                                                    <p>
                                                        Business Name: <span>{receiptInfo?.client?.businessInformation?.name}</span>
                                                    </p>
                                                }
                                                {
                                                    receiptInfo?.client?.businessInformation &&
                                                    <p>
                                                        Business
                                                        Address: <span>{receiptInfo?.client?.businessInformation?.address}, {receiptInfo?.client?.businessInformation?.city}, {receiptInfo?.client?.businessInformation?.province}, {receiptInfo?.client?.businessInformation?.country}, {receiptInfo?.client?.businessInformation?.postalCode}</span>
                                                    </p>
                                                }
                                                {
                                                    receiptInfo?.client?.businessInformation?.email &&
                                                    <p>
                                                        Business Email: <span>{receiptInfo?.client?.businessInformation?.email}</span>
                                                    </p>
                                                }
                                                {
                                                    receiptInfo?.client?.businessInformation?.phone &&
                                                    <p>
                                                        Business Phone: <span>{formatPhoneNumber(receiptInfo?.client?.businessInformation?.phone)}</span>
                                                    </p>
                                                }
                                                {
                                                    receiptInfo?.client?.businessInformation?.website &&
                                                    <p>
                                                        Business Website: <span>{Utils.formatLinkUrlDisplay(receiptInfo?.client?.businessInformation?.website)}</span>
                                                    </p>
                                                }
                                                <br/>
                                                {
                                                    receiptInfo?.client?.invoiceReference &&
                                                    <p>
                                                        invoiceReference: <span>{receiptInfo?.client?.invoiceReference}</span>
                                                    </p>
                                                }
                                                {
                                                    receiptInfo?.client?.amount &&
                                                    <p>
                                                        amount: <span>{formatMoney(receiptInfo?.client?.amount)}</span>
                                                    </p>
                                                }
                                                {
                                                    receiptInfo?.client?.type &&
                                                    <p>
                                                        type: <span>{receiptInfo?.client?.type}</span>
                                                    </p>
                                                }
                                                {
                                                    receiptInfo?.client?.rawSubmittedDateTime &&
                                                    <p>
                                                        submit Date: <span>
                                                        {
                                                            `${DateUtils.instance(receiptInfo?.client?.rawSubmittedDateTime).format(DateUtils.dateTimeFormat)} (${DateUtils.instance(receiptInfo?.client?.rawSubmittedDateTime).offsetName()})`
                                                        }
                                                    </span>
                                                    </p>
                                                }
                                                {
                                                    receiptInfo?.client?.referenceNumber &&
                                                    <p>
                                                        reference Number: <span>{receiptInfo?.client?.referenceNumber}</span>
                                                    </p>
                                                }
                                                {
                                                    receiptInfo?.client?.authCode &&
                                                    <p>
                                                        auth Code: <span>{receiptInfo?.client?.authCode}</span>
                                                    </p>
                                                }
                                                {
                                                    receiptInfo?.client?.lastFourDigits &&
                                                    <p>
                                                        last Four Digits: <span>{receiptInfo?.client?.lastFourDigits}</span>
                                                    </p>
                                                }
                                                <br/>
                                                {
                                                    receiptInfo?.client?.iso &&
                                                    <p>
                                                        iso: <span>{receiptInfo?.client?.iso}</span>
                                                    </p>
                                                }
                                                {
                                                    receiptInfo?.client?.responseCode &&
                                                    <p>
                                                        response Code: <span>{receiptInfo?.client?.responseCode}</span>
                                                    </p>
                                                }
                                                {
                                                    receiptInfo?.client?.terminalMessage &&
                                                    <p>
                                                        Message: <span>{receiptInfo?.client?.terminalMessage}</span>
                                                    </p>
                                                }
                                                {
                                                    receiptInfo?.client?.receiptType &&
                                                    <p>
                                                        receipt Type: <span>{receiptInfo?.client?.receiptType}</span>
                                                    </p>
                                                }
                                            </div>
                                        </Card>
                                    </Grid2>
                                }
                            </Grid2>
                        </>
                    }
                </div>
            </DialogContent>
            <BizPrintTemplatesDialog
                open={bizPrintDialogState.open}
                close={() => setBizPrintDialogState(initialBizPrintDialogState)}
                pageName={bizPrintDialogState.pageName}
                pageData={bizPrintDialogState.pageData}
            />
        </>
    );
}

export default TransactionReceiptInformation;
